<template>
    <section>
        <div v-if="!serverPresenceChecked" class="notCheckedYet">
            <font-awesome-icon icon="spinner-third" size="4x" spin></font-awesome-icon>
            <div>{{$t('checkingServer')}}</div>
        </div>
        <div v-if="serverPresenceChecked">

            <div v-if="shouldShowLoginForm" class="loginForm">
                <b-container>
                    <b-row>
                        <b-col cols="2"></b-col>
                        <b-col cols="8">
                            <b-card :title="$t('loginFormTitle')" class="form">
                                <div class="loginServerInfo">{{$t('loginServerInfo', { server: serverInfo })}}</div>
                                <b-form @submit.prevent="">
                                <b-form-group :label="$t('username')">
                                    <b-input v-model.trim="username" :placeholder="$t('usernamePlaceholder')"
                                            :state="getInputState($v.username)" @blur="checkFor2FA"
                                            @input="$v.username.$touch" ref="username" autofocus
                                            autocomplete="username"></b-input>
                                    <input-feedback :validation="$v.username" use-error></input-feedback>
                                </b-form-group>

                                <b-form-group :label="$t('password')">
                                    <password-field v-model.trim="password" :placeholder="$t('passwordPlaceholder')"
                                            :state="getInputState($v.password)" @input="$v.password.$touch"
                                            autocomplete="password"></password-field>
                                    <input-feedback :validation="$v.password" use-error></input-feedback>
                                </b-form-group>

                                <b-form-group :label="$t('tfaPassword')" v-show="need2fa">
                                    <password-field v-model="tfaPassword" :placeholder="$t('tfaPasswordPlaceholder')"
                                            :state="getInputState($v.tfaPassword)"
                                            @input="$v.tfaPassword.$touch" maxlength="6"
                                            autocomplete="off"></password-field>
                                    <input-feedback :validation="$v.tfaPassword" use-error></input-feedback>
                                </b-form-group>

                                <b-form-group :label="$t('language')">
                                    <b-select v-model="language" :options="languageOptions"
                                            @input="changeLanguage"></b-select>
                                </b-form-group>

                                <b-button type="submit" :disabled="$v.$invalid || loginPending" @click="login"
                                variant="primary">
                                    {{$t('loginButton')}}
                                    <font-awesome-icon icon="spinner-third" spin v-if="loginPending"/>
                                </b-button>
                                <span class="loginError">{{ errorReason }}</span>
                                </b-form>

                            </b-card>

                        </b-col>
                    </b-row>
                </b-container>
            </div>
            <div v-if="!shouldShowLoginForm" class="serverNotAvailable">
                <b-card :title="$t('serverNotAvailableTitle')" text-variant="danger">
                    {{$t('serverNotAvailable')}}
                </b-card>
            </div>

        </div>
    </section>
</template>

<script>
import { sha512 } from 'js-sha512';
import PasswordField from '@/components/passwordField';
import { required, requiredIf, minLength, maxLength } from 'vuelidate/lib/validators';
import InputFeedback from '@/components/inputFeedback';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import i18next from 'i18next';
import axios from 'axios';

export default {
  name: 'login',
  components: { InputFeedback, PasswordField, FontAwesomeIcon },
  i18nOptions: { keyPrefix: 'login' },
  data() {
    return {
      serveraddr: this.$route.query.server || localStorage.getItem('lastcredsserver') || window.location.hostname,
      serverport: this.$route.query.port || localStorage.getItem('lastcredsport') || window.location.port,
      serverprotocol: this.$route.query.protocol || localStorage.getItem('lastcredsprotocol') ||
        window.location.protocol,
      username: '',
      password: '',
      tfaPassword: '',
      need2fa: false,
      interval: null,
      serverPresent: false,
      serverPresenceChecked: false,
      submitting: false,
      loginError: null,
      languages: {},
      language: ''
    };
  },
  validations: {
    username: { required },
    password: { required },
    tfaPassword: { required: requiredIf('need2fa'), minLength: minLength(6), maxLength: maxLength(6) }
  },
  watch: {
    serverPresent(newVal) {
      if (newVal === true) {
        axios.get(`${this.baseURL}/languages/user`)
          .then(d => {
            console.log('getLanguages', d.data);
            this.languages = d.data;
            this.$store.commit('setAvailableLanguages', { languages: d.data });
          });
      }
    }
  },
  created() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    this.poller();
    this.interval = setInterval(this.poller, 1000);
    this.language = this.$store.state.language.toLowerCase();
  },
  beforeDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    this.interval = null;
  },
  methods: {
    poller() {
      axios.get(`${this.baseURL}/presence`)
        .then(d => {
          this.serverPresent = true;
        })
        .catch(e => {
          this.serverPresent = false;
        })
        .finally(() => {
          this.serverPresenceChecked = true;
        });
    },
    changeLanguage(lang) {
      this.$store.dispatch('setLanguage', { language: lang });
    },
    checkFor2FA() {
      if (this.username === '') {
        this.need2fa = false;
        return;
      }
      axios.get(`${this.baseURL}/tfacheck`, { params: { account: this.username } })
        .then(d => {
          this.need2fa = d.data.tfarequired;
        })
        .catch(() => {
          this.need2fa = false;
        });
    },
    login() {
      this.loginError = null;

      axios.get(`${this.baseURL}/authkey`)
        .then(d => {
          this.submitting = true;
          console.log('got auth key', d.data);
          const authKey = d.data.authkey;

          const encodedPassword = sha512.hmac(authKey, this.password);
          const tfaPassword = this.need2fa ? this.tfaPassword : '';

          this.$store
            .dispatch('doLogin', {
              username: this.username,
              encodedPassword: encodedPassword,
              authKey: authKey,
              tfaPassword: tfaPassword,
              serveraddr: this.serveraddr,
              serverport: this.serverport,
              serverprotocol: this.serverprotocol
            })
            .then(() => {
              this.$router.push('/');
            })
            .catch(e => {
              this.loginError = e;
              console.dir(e);
            })
            .finally(() => {
              this.submitting = false;
            });
        });
    },
    getInputState(validInfo) {
      if (!validInfo.$dirty) {
        return null;
      } else {
        return !validInfo.$error;
      }
    }
  },
  computed: {
    baseURL() {
      return `${this.serverprotocol}//${this.serveraddr}:${this.serverport}/rest/login`;
    },
    errorReason() {
      if (this.loginError) {
        if ((this.loginError.response.data) && (this.loginError.response.data.error)) {
          return this.$t(`loginErrors.${this.loginError.response.data.error}`, { error: this.loginError.message });
        } else {
          return this.$t('loginError', { error: this.loginError.message });
        }
      } else {
        return '';
      }
    },
    loginPending() {
      return this.submitting || this.$store.state.loginPending;
    },
    shouldShowLoginForm() {
      return this.serverPresent;
    },
    serverInfo() {
      if ((this.serveraddr !== window.location.hostname) || (this.serverport !== window.location.port)) {
        if (((this.serverport === '80') && (this.serverprotocol === 'http:')) ||
            ((this.serverport === '443') && (this.serverprotocol === 'https:'))) {
          return this.$t('serverInfoDefaultPort', { protocol: this.serverprotocol,
            addr: this.serveraddr,
            port: this.serverport });
        } else {
          return this.$t('serverInfo', { protocol: this.serverprotocol,
            addr: this.serveraddr,
            port: this.serverport });
        }
      } else {
        return '';
      }
      // <p>Login to {{ serverprotocol }}//{{ serveraddr }}:{{ serverport }} - {{serverPresent}}</p>
    },
    languageOptions() {
      return Object.keys(this.languages).map(l => {
        return { text: this.languages[l].name, value: l.toLowerCase() };
      });
    },
    i18nextLanguage() {
      console.log(this.language);
      return i18next.language;
    },
    i18nextLanguages() {
      console.log(this.language);
      return i18next.languages;
    }
  }
};
</script>

<style scoped lang="scss">
    @import '../assets/bootstrap-style.scss';

    section {
        padding: 10px 30px;

        .loginForm {
            position: absolute;
            top: 50%;
            width: 100%;
            transform: translateY(-50%);
            .form {
                box-shadow: 0px 0px 10px 0px rgba(0,0,0,0.75);
            }
            .loginServerInfo {
                font-style: italic;
                margin-bottom: 0.5em;
            }
            .loginError {
                color: $danger;
                margin-left: 20px;
            }
        }

        .notCheckedYet {
            opacity: 0.2;
            position: absolute;
            top: 50%;
            text-align: center;
            width: 100%;
            transform: translateY(-50%);

        }
    }
</style>
